import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '@/hooks/useDateLocale';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2, Search, X, ChevronRight, Navigation, ArrowDownAZ, Signal, Clock,
} from 'lucide-react';

import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { DeviceMap } from '@/components/dashboard/DeviceMap';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { useTraccarWebSocket } from '@/hooks/useTraccarWebSocket';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { fetchDevices, fetchPositions, fetchPermissionsMap, userDevicesFromMap, type Position } from '@/lib/traccar-api';
import { DeviceRowContent } from '@/components/devices/DeviceRowContent';
import { useResellerAccess } from '@/hooks/useResellerAccess';
import { fetchDeviceMerges, fetchCustomers } from '@/lib/reseller-api';
import { cn } from '@/lib/utils';

const LiveMap = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const { isAuthenticated } = useTraccarSession();

  // WebSocket for real-time updates
  const {
    positions: wsPositions,
    devices: wsDevices,
    isConnected,
    connect: wsConnect,
    disconnect: wsDisconnect,
  } = useTraccarWebSocket();

  // Initial data fetch
  const { data: initialDevices = [], isLoading: devicesLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    enabled: isAuthenticated,
  });

  const { data: initialPositions = [], isLoading: positionsLoading } = useQuery({
    queryKey: ['positions'],
    queryFn: fetchPositions,
    enabled: isAuthenticated,
    staleTime: 10000,
  });

  // Merge WS devices with initial
  const allDevices = useMemo(() => {
    if (wsDevices.length === 0) return initialDevices;
    const deviceMap = new Map(initialDevices.map(d => [d.id, d]));
    wsDevices.forEach(d => deviceMap.set(d.id, d));
    return Array.from(deviceMap.values());
  }, [initialDevices, wsDevices]);

  const { isReseller } = useResellerAccess();
  const { data: deviceMerges = [] } = useQuery({
    queryKey: ['device-merges'],
    queryFn: fetchDeviceMerges,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  const mergeSecondaryIds = useMemo(() => {
    const set = new Set<number>();
    deviceMerges.forEach(m => set.add(m.secondary_device_id));
    return set;
  }, [deviceMerges]);
  const devices = useMemo(() => {
    if (isReseller) return allDevices;
    return allDevices.filter(d => !mergeSecondaryIds.has(d.id));
  }, [allDevices, isReseller, mergeSecondaryIds]);

  // Merge WS positions with initial
  const [positions, setPositions] = useState<Position[]>([]);

  useEffect(() => {
    if (initialPositions.length > 0 && positions.length === 0) {
      setPositions(initialPositions);
    }
  }, [initialPositions, positions.length]);

  useEffect(() => {
    if (wsPositions.length > 0) {
      setPositions(prev => {
        const updated = new Map(prev.map(p => [p.deviceId, p]));
        wsPositions.forEach(p => updated.set(p.deviceId, p));
        return Array.from(updated.values());
      });
    }
  }, [wsPositions]);

  // Connect WebSocket on mount
  useEffect(() => {
    wsConnect();
    return () => { wsDisconnect(); };
  }, [wsConnect, wsDisconnect]);

  // Device → customer name map (reseller only)
  const [deviceCustomerMap, setDeviceCustomerMap] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    if (!isReseller) return;
    (async () => {
      try {
        const [customers, allDevices, perms] = await Promise.all([
          fetchCustomers(),
          fetchDevices(),
          fetchPermissionsMap(),
        ]);
        const map = new Map<number, string>();
        for (const c of customers.filter(c => c.traccar_user_id)) {
          const devs = userDevicesFromMap(c.traccar_user_id!, allDevices, perms);
          for (const d of devs) map.set(d.id, c.name);
        }
        setDeviceCustomerMap(map);
      } catch { /* ignore */ }
    })();
  }, [isReseller]);

  // Device list state
  const [keyword, setKeyword] = useState('');
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [sortOrder, setSortOrder] = useState<'status' | 'name' | 'speed' | 'recent'>('status');
  const [statusFilter, setStatusFilter] = useState<'moving' | 'idle' | 'parked' | 'offline' | null>(null);
  const [controlsEl, setControlsEl] = useState<HTMLElement | null>(null);

  // For non-resellers: use the most recent position from primary or secondary merged device
  const effectivePositions = useMemo(() => {
    if (isReseller) return positions;
    const posMap = new Map(positions.map(p => [p.deviceId, p]));
    deviceMerges.forEach(merge => {
      const primaryPos = posMap.get(merge.primary_device_id);
      const secondaryPos = posMap.get(merge.secondary_device_id);
      if (secondaryPos) {
        const primaryTime = primaryPos ? new Date(primaryPos.fixTime).getTime() : 0;
        const secondaryTime = new Date(secondaryPos.fixTime).getTime();
        if (secondaryTime > primaryTime) {
          posMap.set(merge.primary_device_id, { ...secondaryPos, deviceId: merge.primary_device_id });
        }
      }
    });
    return Array.from(posMap.values());
  }, [positions, isReseller, deviceMerges]);

  // Position lookup
  const positionMap = useMemo(() => {
    const map = new Map<number, Position>();
    effectivePositions.forEach(p => map.set(p.deviceId, p));
    return map;
  }, [effectivePositions]);

  // Filtered & sorted devices
  const filteredDevices = useMemo(() => {
    let result = devices;
    if (keyword.trim()) {
      const kw = keyword.toLowerCase();
      result = result.filter(d =>
        d.name.toLowerCase().includes(kw) ||
        d.uniqueId.toLowerCase().includes(kw) ||
        (deviceCustomerMap.get(d.id) ?? '').toLowerCase().includes(kw)
      );
    }
    if (statusFilter) {
      result = result.filter(d => {
        const isOnline = d.status === 'online';
        const pos = positionMap.get(d.id);
        const isMoving = isOnline && pos && pos.speed > 0;
        const isIdle = isOnline && !isMoving && !!pos?.attributes?.ignition;
        if (statusFilter === 'moving') return !!isMoving;
        if (statusFilter === 'idle') return !!isIdle;
        if (statusFilter === 'parked') return isOnline && !isMoving && !isIdle;
        if (statusFilter === 'offline') return !isOnline;
        return true;
      });
    }
    return [...result].sort((a, b) => {
      if (sortOrder === 'name') {
        return a.name.localeCompare(b.name);
      }
      if (sortOrder === 'speed') {
        const speedA = a.status === 'online' ? (positionMap.get(a.id)?.speed ?? 0) : -1;
        const speedB = b.status === 'online' ? (positionMap.get(b.id)?.speed ?? 0) : -1;
        return speedB - speedA;
      }
      if (sortOrder === 'recent') {
        const timeA = a.lastUpdate ? new Date(a.lastUpdate).getTime() : 0;
        const timeB = b.lastUpdate ? new Date(b.lastUpdate).getTime() : 0;
        return timeB - timeA;
      }
      // status: moving → idle → offline, then most recent lastUpdate
      const statusRank = (d: typeof a) => {
        if (d.status !== 'online') return 2;
        return (positionMap.get(d.id)?.speed ?? 0) > 0 ? 0 : 1;
      };
      const diff = statusRank(a) - statusRank(b);
      if (diff !== 0) return diff;
      const timeA = positionMap.get(a.id)?.fixTime
        ? new Date(positionMap.get(a.id)!.fixTime).getTime()
        : (a.lastUpdate ? new Date(a.lastUpdate).getTime() : 0);
      const timeB = positionMap.get(b.id)?.fixTime
        ? new Date(positionMap.get(b.id)!.fixTime).getTime()
        : (b.lastUpdate ? new Date(b.lastUpdate).getTime() : 0);
      return timeB - timeA;
    });
  }, [devices, keyword, sortOrder, statusFilter, positionMap, deviceCustomerMap]);

  // Status counts
  const statusCounts = useMemo(() => {
    let moving = 0, idle = 0, parked = 0, offline = 0;
    devices.forEach(d => {
      if (d.status !== 'online') { offline++; return; }
      const pos = positionMap.get(d.id);
      if (pos && pos.speed > 0) moving++;
      else if (pos?.attributes?.ignition) idle++;
      else parked++;
    });
    return { moving, idle, parked, offline, total: devices.length };
  }, [devices, positionMap]);

  const handleDeviceSelect = useCallback((deviceId: number | null) => {
    setSelectedDeviceId(deviceId);
  }, []);

  const isLoading = devicesLoading || positionsLoading;

  if (isLoading && positions.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const vehicleListPanel = (
    <div className={cn(
      "flex flex-col border-r bg-background transition-all duration-300 h-full",
      panelOpen ? "" : "w-0 overflow-hidden border-r-0"
    )}>
      {/* Panel header */}
      <div className="p-3 border-b space-y-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">{t('nav.vehicles')}</h2>
          <div className="flex items-center gap-2">
            {isConnected && (
              <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">
                LIVE
              </Badge>
            )}
            <button
              onClick={() => setSortOrder(s => s === 'status' ? 'name' : s === 'name' ? 'speed' : s === 'speed' ? 'recent' : 'status')}
              className="text-muted-foreground hover:text-foreground"
              title={sortOrder === 'status' ? 'Sort: Status' : sortOrder === 'name' ? 'Sort: A–Z' : sortOrder === 'speed' ? 'Sort: Speed' : 'Sort: Most Recent'}
            >
              {sortOrder === 'name' ? <ArrowDownAZ className="h-4 w-4" /> : sortOrder === 'speed' ? <Signal className="h-4 w-4" /> : sortOrder === 'recent' ? <Clock className="h-4 w-4" /> : <Signal className="h-4 w-4 opacity-50" />}
            </button>
            <button onClick={() => setPanelOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('devices.searchPlaceholder')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        {/* Status summary */}
        <div className="flex gap-1 text-xs w-full">
          {([
            { key: 'moving', color: 'bg-green-500', count: statusCounts.moving, label: t('map.moving').toLowerCase() },
            { key: 'idle', color: 'bg-yellow-500', count: statusCounts.idle, label: t('map.idle').toLowerCase() },
            { key: 'parked', color: 'bg-orange-500', count: statusCounts.parked, label: t('map.parked').toLowerCase() },
            { key: 'offline', color: 'bg-red-500', count: statusCounts.offline, label: t('map.offline').toLowerCase() },
          ] as const).map(({ key, color, count, label }) => (
            <button
              key={key}
              title={`${count} ${label}`}
              onClick={() => setStatusFilter(f => f === key ? null : key)}
              className={cn(
                "flex items-center gap-1 rounded px-1 min-w-0 flex-1 transition-colors",
                statusFilter === key ? "bg-muted font-semibold" : "hover:bg-muted/50"
              )}
            >
              <span className={cn("w-2 h-2 rounded-full flex-shrink-0", color)} />
              <span className="tabular-nums">{count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Device list */}
      <ScrollArea className="flex-1">
        <div className="divide-y">
          {filteredDevices.map(device => {
            const pos = positionMap.get(device.id);
            const isOnline = device.status === 'online';
            const isMoving = isOnline && pos && pos.speed > 0;
            const isIdle = isOnline && !isMoving && !!pos?.attributes?.ignition;
            const speedKmh = pos ? Math.round(pos.speed * 1.852) : 0;
            const isSelected = selectedDeviceId === device.id;
            const course = pos?.course;

            return (
              <div
                key={device.id}
                className={cn(
                  "w-80 px-3 py-2.5 cursor-pointer transition-colors hover:bg-muted/50",
                  isSelected && "bg-primary/5 border-l-2 border-l-primary"
                )}
                onClick={() => handleDeviceSelect(device.id)}
              >
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "w-2.5 h-2.5 rounded-full flex-shrink-0",
                    isMoving ? "bg-green-500" : isIdle ? "bg-yellow-500" : isOnline ? "bg-orange-500" : "bg-red-500"
                  )} />
                  <span className="text-sm font-medium truncate flex-1">{device.name}</span>
                  {deviceCustomerMap.get(device.id) && (
                    <span className="text-[10px] text-muted-foreground truncate max-w-[80px]" title={deviceCustomerMap.get(device.id)}>
                      {deviceCustomerMap.get(device.id)}
                    </span>
                  )}
                  {isOnline && (
                    <span className={cn(
                      "text-xs font-mono flex items-center gap-1",
                      isMoving ? "text-green-600" : isIdle ? "text-yellow-600" : "text-orange-600"
                    )}>
                      {isMoving && <Navigation className="h-3 w-3" style={{ transform: `rotate(${(course - 45)}deg)` }} />}
                      {speedKmh} km/h
                    </span>
                  )}
                </div>

                <DeviceRowContent
                  device={device}
                  position={pos}
                  locale={dateFnsLocale}
                  t={t}
                />
              </div>
            );
          })}

          {filteredDevices.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('devices.noDevices')}</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <DashboardLayout transparentHeader headerActions={<div ref={setControlsEl} className="flex items-center gap-1" />} sidebar={vehicleListPanel}>
      <div className="h-full relative">
        {/* Toggle panel button (when closed) */}
        {!panelOpen && (
          <button
            onClick={() => setPanelOpen(true)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-background border rounded-r-lg px-1 py-3 shadow-md hover:bg-muted/50 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
        <DeviceMap
          devices={devices}
          positions={effectivePositions}
          selectedDeviceId={selectedDeviceId}
          onDeviceSelect={handleDeviceSelect}
          compact={true}
          controlsContainer={controlsEl}
        />
      </div>
    </DashboardLayout>
  );
};

export default LiveMap;
